import React, { useMemo, useState } from "react";
import Modal from "react-modal";
import {
    FaFacebookF,
    FaInstagram,
    FaLinkedinIn,
    FaTiktok,
    FaInfoCircle,
} from "react-icons/fa";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { Link } from "react-scroll";
import ScrollAnimation from "react-animate-on-scroll";
import "animate.css";

import DOMPurify from "dompurify";


// Генерация форм карточек:
// none  — без скругления
// left  — скругление слева
// right — скругление справа
// два одинаковых типа подряд быть не может
const generateSpeakerShapes = (speakers) => {
    const types = ["none", "left", "right"];
    const result = {};
    let previousType = null;

    speakers.forEach((speaker, index) => {
        const availableTypes = types.filter((type) => type !== previousType);

        const currentType =
            availableTypes[Math.floor(Math.random() * availableTypes.length)];

        const speakerKey =
            speaker.ID ??
            speaker.id ??
            speaker.post_id ??
            `${speaker.post_title}-${index}`;

        result[speakerKey] = currentType;

        previousType = currentType;
    });

    return result;
};


// СоцСети
const SocialIcons = ({
    fbLink,
    instLink,
    linkedinLink,
    tiktokLink,
    openModal,
    sociconColors,
}) => {
    const socialLinks = [
        {
            link: fbLink,
            icon: <FaFacebookF />,
        },
        {
            link: instLink,
            icon: <FaInstagram />,
        },
        {
            link: linkedinLink,
            icon: <FaLinkedinIn />,
        },
        {
            link: tiktokLink,
            icon: <FaTiktok />,
        },
    ];

    const iconClass = `
        flex size-[30px] items-center justify-center rounded-full
        text-black transition-transform duration-300 hover:scale-105
        [&>svg]:size-[16px] sm:[&>svg]:size-[18px]
    `;

    return (
        <div className="absolute left-[10px] top-[70px] z-[40] flex flex-col gap-[12px] sm:gap-[10px] lg:left-[15px]">
            {socialLinks.map((social, index) =>
                social.link ? (
                    <a
                        key={index}
                        href={social.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={iconClass}
                        style={{ backgroundColor: sociconColors }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {social.icon}
                    </a>
                ) : null
            )}

            <button
                type="button"
                className={iconClass}
                style={{ backgroundColor: sociconColors }}
                onClick={(e) => {
                    e.stopPropagation();
                    openModal();
                }}
            >
                <FaInfoCircle />
            </button>
        </div>
    );
};


// Модалка Спикера
const SpeakerModal = ({
    speaker,
    isOpen,
    closeModal,
    SpeacerFonColor,
}) => {
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={closeModal}
            ariaHideApp={false}
            className="modal-container fixed bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-black bg-opacity-50"
        >
            <button
                onClick={closeModal}
                className="absolute right-0 top-0 p-4 text-red-800 hover:text-red-600 focus:outline-none"
            >
                <IoMdCloseCircleOutline className="text-4xl text-red-800" />
            </button>

            <div className="m-9 w-full overflow-hidden bg-white p-4 shadow-lg sm:max-w-4xl sm:p-6">
                <div className="h-[80vh] max-h-max overflow-y-auto">
                    <div className="flex flex-col items-center md:flex-row md:items-start">
                        <div
                            className="mb-4 overflow-hidden md:mb-0 md:w-4/12"
                            style={{
                                backgroundColor: SpeacerFonColor,
                            }}
                        >
                            <img
                                src={speaker.acf.sm_image}
                                alt={speaker.post_title}
                                className="h-auto w-full"
                            />
                        </div>

                        <div className="w-full sm:w-auto md:w-4/6 md:pl-6">
                            <div className="mb-4 border-b-2 pb-4">
                                <h3 className="text-center text-xl font-semibold md:text-left sm:text-3xl mb-3">
                                    {speaker.post_title}
                                </h3>

                                {speaker.acf.logo && (
                                    <img
                                        src={speaker.acf.logo}
                                        alt="Logo"
                                        className="m-auto mb-4 w-16 object-contain md:ml-0"
                                        style={{
                                            width:
                                                speaker.acf.logo_mw + "px",
                                        }}
                                    />
                                )}

                                <p className="text-center text-sm text-gray-600 md:text-left">
                                    {speaker.acf.position}
                                </p>
                            </div>

                            <div
                                className="speacer-popup-info"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        speaker.acf.info,
                                        {
                                            sanitize: true,
                                        }
                                    ),
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};


// Карточка Спикера
const SpeakerCard = ({ speaker, shape }) => {
    let SpeacerFonColor = "";
    let SociconColor = "";

    if (speaker.acf.select_scene_get === "business") {
        SpeacerFonColor = "#59E6FF";
        SociconColor = "#D4F9FF";
    } else if (speaker.acf.select_scene_get === "marketing") {
        SpeacerFonColor = "#FFD97C";
        SociconColor = "#FFF2D3";
    } else {
        SpeacerFonColor = "#BABAFE";
        SociconColor = "#E6E6FF";
    }

    const shapeClasses = {
        none: "",
        left: "rounded-tl-[clamp(86px,calc(86px+54*((100vw-390px)/1530)),140px)]",
        right: "rounded-tr-[clamp(86px,calc(86px+54*((100vw-390px)/1530)),140px)]",
    };

    const [modalIsOpen, setIsOpen] = React.useState(false);

    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    return (
        <div className="relative flex flex-col items-cente">
            <div
                className={`relative w-full overflow-hidden ${
                    shapeClasses[shape] || ""
                }`}
                style={{
                    backgroundColor: SpeacerFonColor,
                }}
            >
                <img className="relative z-20 mx-auto h-auto w-full transition duration-[400ms]" src={speaker.acf.sm_image} alt={speaker.post_title}/>
            </div>

            <div className="relative z-20 p-4 pt-0 w-full shadow-[0_0_14px_0_rgba(19,32,51,0.12)] flex-grow">
                <div className={ "relative z-[9] rounded-full mx-auto mb-3 flex h-14 w-14 justify-center p-2 shadow-xl sm:-mt-[40px] sm:h-[80px] sm:w-[80px] -mt-[28px]" +
                        (speaker.acf.logo ? "" : " opacity-0")
                    }
                >
                    {speaker.acf.logo ? (
                        <img src={speaker.acf.logo} alt="Logo" className="relative z-20 block w-full object-contain"
                            style={{
                                width: speaker.acf.logo_mw + "px",
                            }}
                        />
                    ) : (
                        ""
                    )}

                    <div className="absolute bottom-0 rounded-full  left-0 z-10 h-full w-full border-[1px] bg-white shadow-lg shadow-gray-200" />
                </div>

                <h3 className="mb-2 text-center font-semibold max-sm:leading-5 sm:text-lg">
                    {speaker.post_title}
                </h3>

                <p className="text-center text-[13px] text-gray-600 max-sm:leading-4 sm:text-sm">
                    {speaker.acf.position}
                </p>
            </div>

            <button
                onClick={openModal}
                className="absolute h-full w-full z-[30]"
                aria-label={`Відкрити інформацію про ${speaker.post_title}`}
            />

            <SpeakerModal
                speaker={speaker}
                isOpen={modalIsOpen}
                closeModal={closeModal}
                SpeacerFonColor={SpeacerFonColor}
            />

            <SocialIcons
                fbLink={speaker.acf.fb_link}
                instLink={speaker.acf.inst_link}
                linkedinLink={speaker.acf.linkedin_link}
                tiktokLink={speaker.acf.tiktok_link}
                openModal={openModal}
                sociconColors={SociconColor}
            />
        </div>
    );
};


// Основной компонент для отображения спикеров
const EventSpeakersBase = ({ speacersData }) => {
    const [scene, setScene] = useState("all");
    const [isExpanded, setIsExpanded] = useState(false);

    const speakerShapes = useMemo(() => {
        if (!speacersData || !speacersData.length) {
            return {};
        }

        return generateSpeakerShapes(speacersData);
    }, [speacersData]);

    const ArrowIcon = () => (
        <div className="absolute right-2 top-2 w-[25px] xl:w-[30px]">
            <svg
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M30.0066 0.011818V29.8169H18.9594V18.7685L7.86502 29.8258L0 21.6433L11.0722 11.0381H0.150818V0H29.9947V0.011818H30.0066Z"
                    fill="white"
                />
            </svg>
        </div>
    );

    const changeScene = (sceneType) => {
        setScene(sceneType);
    };

    const toggleSpeakersVisibility = () => {
        setIsExpanded(!isExpanded);
    };

    const scenes = [
        {
            id: "all",
            title: "Дивитись всіх",
            bg: "bg-[#00007c]",
            text: "text-white",
        },
        {
            id: "main",
            title: "Головна сцена",
            bg: "bg-[#babafe]",
            text: "text-black",
        },
        {
            id: "marketing",
            title: "Сцена маркетингових стратегій",
            bg: "bg-[#ffd97c]",
            text: "text-black",
        },
        {
            id: "business",
            title: "Сцена бізнес-розвитку",
            bg: "bg-[#59e6ff]",
            text: "text-black",
        },
    ];

    return (
        <>
            {speacersData && speacersData.length > 0 ? (
                <section className="relative  pb-[60px] lg:pb-[120px] bg-[#faf9ff]" id="speacers">
                    <div className="container mx-auto max-w-full px-[clamp(20px,calc(20px+130*((100vw-390px)/1530)),150px)]">
                        <h2 className="text-[clamp(34px,calc(34px+53*((100vw-390px)/1530)),87px)] 
                            mb-[25px] md:mb-[40px] xl:mb-[66px]
                 font-black text-[#26265d]  uppercase leading-[1.2]">Спікери 2025</h2>

                        {/* <div className="mb-8 md:mb-[40px] xl:mb-[60px] flex flex-col justify-between gap-[10px] md:flex-wrap lg:flex-row xl:gap-[42px]">
                            {scenes.map((item) => (
                                <Link
                                    key={item.id}
                                    to="speacers"
                                    smooth={true}
                                    offset={-70}
                                    duration={500}
                                    onClick={() =>
                                        changeScene(item.id)
                                    }
                                    className={`relative flex min-h-[50px] items-center px-5 pr-10
                                        text-base font-bold uppercase leading-[1.2]
                                        lg:flex-1 xl:min-h-[60px] xl:pl-[30px]
                                        ${item.bg} ${item.text}`}
                                >
                                    {item.title}

                                    <ArrowIcon />
                                </Link>
                            ))}
                        </div> */}

                        <div className={`grid grid-cols-2 gap-6 overflow-hidden pb-6 transition-all duration-300
                                sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6
                                ${
                                    !isExpanded
                                        ? "max-h-[1200px] sm:max-h-[1450px]"
                                        : "max-h-none"
                                }`}>
                            {speacersData
                                .filter(
                                    (speaker) =>
                                        scene === "all" ||
                                        speaker.acf.select_scene_get ===
                                            scene
                                )
                                .map((speaker, index) => {
                                    const speakerKey =
                                        speaker.ID ??
                                        speaker.id ??
                                        speaker.post_id ??
                                        `${speaker.post_title}-${index}`;

                                    return (
                                        <SpeakerCard
                                            key={speakerKey}
                                            speaker={speaker}
                                            shape={
                                                speakerShapes[
                                                    speakerKey
                                                ]
                                            }
                                        />
                                    );
                                })}
                        </div>

                        <div className="mt-10 text-center">
                            <Link
                                to="speacers"
                                smooth={true}
                                offset={-70}
                                duration={500}
                                onClick={toggleSpeakersVisibility}
                                className="bg-[#ffd97c] px-[50px] py-[14px] text-center
                                    text-[20px] font-bold uppercase tracking-widest text-black"
                            >
                                {isExpanded
                                    ? "Сховати"
                                    : "Всі спікери"}
                            </Link>
                        </div>
                    </div>
                </section>
            ) : null}
        </>
    );
};

export default EventSpeakersBase;